import { DEFAULT_LEAD_SOURCE, resolveLeadSource } from '../constants/lead-source.constants';
import { displayContactEmail } from '../utils/contact-display.util';
import { CreateLeadPayload, LeadDto, UpdateLeadPayload } from '../services/leads.service';

export type LeadFormRequiredField =
  | 'fullName'
  | 'phoneNumber'
  | 'email'
  | 'leadSource'
  | 'preferredDestination';

export type LeadFormFieldErrors = Partial<Record<LeadFormRequiredField, string>>;

export type LeadFormFields = {
  fullName: string;
  email: string;
  phoneNumber: string;
  whatsappNumber: string;
  city: string;
  state: string;
  country: string;
  qualification: string;
  percentageOrCgpa: string;
  passingYear: string;
  institution: string;
  preferredDestination: string;
  preferredCourse: string;
  intake: string;
  budgetRange: string;
  passportStatus: string;
  englishTestStatus: string;
  loanRequired: string;
  stream: string;
  section: string;
  leadSource: string;
  message: string;
};

export function emptyLeadForm(): LeadFormFields {
  return {
    fullName: '',
    email: '',
    phoneNumber: '',
    whatsappNumber: '',
    city: '',
    state: '',
    country: '',
    qualification: '',
    percentageOrCgpa: '',
    passingYear: '',
    institution: '',
    preferredDestination: '',
    preferredCourse: '',
    intake: '',
    budgetRange: '',
    passportStatus: '',
    englishTestStatus: '',
    loanRequired: '',
    stream: '',
    section: '',
    leadSource: DEFAULT_LEAD_SOURCE,
    message: '',
  };
}

export function leadFormFromLead(lead: LeadDto): LeadFormFields {
  return {
    fullName: lead.fullName,
    email: displayContactEmail(lead.email),
    phoneNumber: lead.phoneNumber,
    whatsappNumber: lead.whatsappNumber ?? '',
    city: lead.city ?? '',
    state: lead.state ?? '',
    country: lead.country ?? '',
    qualification: lead.qualification ?? '',
    percentageOrCgpa: lead.percentageOrCgpa ?? '',
    passingYear: lead.passingYear ?? '',
    institution: lead.institution ?? '',
    preferredDestination: lead.preferredDestination ?? '',
    preferredCourse: lead.preferredCourse ?? '',
    intake: lead.intake ?? '',
    budgetRange: lead.budgetRange ?? '',
    passportStatus: lead.passportStatus ?? '',
    englishTestStatus: lead.englishTestStatus ?? '',
    loanRequired: lead.loanRequired ?? '',
    stream: lead.stream ?? '',
    section: lead.section ?? '',
    leadSource: resolveLeadSource(lead.leadSource),
    message: lead.message ?? '',
  };
}

function trimOptional(value?: string): string | undefined {
  const trimmed = value?.trim();
  return trimmed || undefined;
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function validateRequiredLeadFormFields(form: LeadFormFields): LeadFormFieldErrors {
  const errors: LeadFormFieldErrors = {};

  if (!form.fullName.trim()) {
    errors.fullName = 'Full name is required.';
  }
  if (!form.phoneNumber.trim()) {
    errors.phoneNumber = 'Phone is required.';
  }
  if (form.email.trim() && !isValidEmail(form.email)) {
    errors.email = 'Enter a valid email address.';
  }
  if (!form.leadSource.trim()) {
    errors.leadSource = 'Lead source is required.';
  }
  if (!form.preferredDestination.trim()) {
    errors.preferredDestination = 'Preferred destination is required.';
  }

  return errors;
}

export function validateApplicantLeadFormFields(form: LeadFormFields): LeadFormFieldErrors {
  const errors = validateRequiredLeadFormFields(form);
  if (!form.email.trim()) {
    delete errors.email;
  }
  return errors;
}

export function extractLeadFormApiErrors(
  errorBody: { errors?: unknown; message?: unknown } | null | undefined,
): LeadFormFieldErrors {
  const fromErrors = mapLeadFormApiErrors(errorBody?.errors);
  if (hasLeadFormFieldErrors(fromErrors)) return fromErrors;
  return mapLeadFormApiErrors(errorBody?.message);
}

const LEAD_FORM_API_FIELD_KEYS: LeadFormRequiredField[] = [
  'fullName',
  'phoneNumber',
  'email',
  'leadSource',
  'preferredDestination',
];

export function mapLeadFormApiErrors(errors: unknown): LeadFormFieldErrors {
  if (!Array.isArray(errors)) return {};

  const mapped: LeadFormFieldErrors = {};
  for (const item of errors) {
    const text = String(item);
    const lower = text.toLowerCase();
    for (const field of LEAD_FORM_API_FIELD_KEYS) {
      if (lower.includes(field.toLowerCase())) {
        mapped[field] = humanizeLeadFormApiError(field, text);
      }
    }
  }
  return mapped;
}

function humanizeLeadFormApiError(field: LeadFormRequiredField, message: string): string {
  const lower = message.toLowerCase();
  if (field === 'email') {
    if (lower.includes('must be an email') || lower.includes('email')) {
      return 'Enter a valid email address.';
    }
  }
  if (lower.includes('should not be empty') || lower.includes('must not be empty')) {
    switch (field) {
      case 'fullName':
        return 'Full name is required.';
      case 'phoneNumber':
        return 'Phone is required.';
      case 'email':
        return 'Email is required.';
      case 'leadSource':
        return 'Lead source is required.';
      case 'preferredDestination':
        return 'Preferred destination is required.';
    }
  }
  if (field === 'leadSource' && lower.includes('must be one of')) {
    return 'Select a valid lead source.';
  }
  return message;
}

export function hasLeadFormFieldErrors(errors: LeadFormFieldErrors): boolean {
  return Object.keys(errors).length > 0;
}

export function leadFormToCreatePayload(form: LeadFormFields): CreateLeadPayload {
  return {
    fullName: form.fullName.trim(),
    email: trimOptional(form.email),
    phoneNumber: form.phoneNumber.trim(),
    whatsappNumber: trimOptional(form.whatsappNumber),
    city: trimOptional(form.city),
    state: trimOptional(form.state),
    country: trimOptional(form.country),
    qualification: trimOptional(form.qualification),
    percentageOrCgpa: trimOptional(form.percentageOrCgpa),
    passingYear: trimOptional(form.passingYear),
    institution: trimOptional(form.institution),
    preferredDestination: form.preferredDestination.trim(),
    leadSource: resolveLeadSource(form.leadSource),
    preferredCourse: trimOptional(form.preferredCourse),
    intake: trimOptional(form.intake),
    budgetRange: trimOptional(form.budgetRange),
    passportStatus: trimOptional(form.passportStatus),
    englishTestStatus: trimOptional(form.englishTestStatus),
    loanRequired: trimOptional(form.loanRequired),
    stream: trimOptional(form.stream),
    section: trimOptional(form.section),
    message: trimOptional(form.message),
  };
}

export function leadFormToUpdatePayload(form: LeadFormFields): UpdateLeadPayload {
  return {
    fullName: form.fullName.trim(),
    email: trimOptional(form.email),
    phoneNumber: form.phoneNumber.trim(),
    whatsappNumber: trimOptional(form.whatsappNumber),
    city: trimOptional(form.city),
    state: trimOptional(form.state),
    country: trimOptional(form.country),
    qualification: trimOptional(form.qualification),
    percentageOrCgpa: trimOptional(form.percentageOrCgpa),
    passingYear: trimOptional(form.passingYear),
    institution: trimOptional(form.institution),
    preferredDestination: trimOptional(form.preferredDestination),
    leadSource: trimOptional(form.leadSource),
    preferredCourse: trimOptional(form.preferredCourse),
    intake: trimOptional(form.intake),
    budgetRange: trimOptional(form.budgetRange),
    passportStatus: trimOptional(form.passportStatus),
    englishTestStatus: trimOptional(form.englishTestStatus),
    loanRequired: trimOptional(form.loanRequired),
    stream: trimOptional(form.stream),
    section: trimOptional(form.section),
    message: trimOptional(form.message),
  };
}
